import React, { useEffect, useMemo, useState } from "react";
import { BookOpen, Receipt, Plus, Palette, Settings as SettingsIcon, Download, Loader2, ArrowUp, ArrowDown, Wallet, Search, Save, RotateCcw, ArrowRight, School, ExternalLink, Trash2, ListOrdered, Send, ShieldCheck, AlertTriangle, CheckCircle2, XCircle, Pencil, Upload, X } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";

const TABS = [
  { key: "journal", label: "Journal", Icon: BookOpen },
  { key: "receipts", label: "Receipts", Icon: Receipt },
  { key: "add", label: "Add receipt", Icon: Plus },
  { key: "schools", label: "Schools", Icon: School },
  { key: "templates", label: "Templates", Icon: Palette },
  { key: "settings", label: "Settings", Icon: SettingsIcon },
];

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const todayLocal = todayIso;

// UPI/QR = paid straight to the bank (no Razorpay verification).
const MODE_OPTIONS = [["cash", "Cash"], ["online", "Online (offline recorded)"], ["upi_qr", "UPI/QR (direct to bank)"]];
const MODE_BADGE = {
  cash: ["CASH", "text-orange-700 bg-orange-100"],
  upi_qr: ["UPI/QR", "text-violet-700 bg-violet-100"],
  online: ["ONLINE", "text-emerald-700 bg-emerald-100"],
};
const ModeBadge = ({ mode }) => {
  const [label, cls] = MODE_BADGE[mode] || MODE_BADGE.online;
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
};

export default function AccountantDashboard() {
  const { user, customRole } = useAuth();
  const [tab, setTab] = useState("journal");
  const [events, setEvents] = useState([]);

  useEffect(() => { (async () => { try { setEvents((await api.get("/events")).data || []); } catch { /* noop */ } })(); }, []);

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="accountant-dashboard">
        <PageHeader
          title={`Books, ${user.name.split(" ")[0]} ✦`}
          subtitle="Receipts, journal, templates & billing settings — everything a professional accountant needs."
          icon={Wallet}
          accent="emerald"
        />

        <div className="flex flex-wrap gap-2 my-6" data-testid="accountant-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border transition ${tab === t.key ? "bg-emerald-500 text-white border-emerald-500 shadow-md" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300"}`}
              data-testid={`accountant-tab-${t.key}`}
            >
              <t.Icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "journal" && <JournalTab events={events} />}
        {tab === "receipts" && <ReceiptsTab events={events} />}
        {tab === "add" && <AddReceiptTab events={events} onCreated={() => setTab("receipts")} />}
        {tab === "schools" && <SchoolsTab events={events} />}
        {tab === "templates" && <TemplatesTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </Layout>
  );
}

// =========================================================
// JOURNAL — Dr/Cr entries with filters + running total
// =========================================================
function JournalTab({ events }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [eventId, setEventId] = useState("");
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ dr: 0, cr: 0 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (start) params.append("start", start);
      if (end) params.append("end", end);
      if (eventId) params.append("event_id", eventId);
      const r = await api.get(`/accountant/journal?${params.toString()}`);
      setEntries(r.data.entries || []);
      setTotals({ dr: r.data.total_dr_paise || 0, cr: r.data.total_cr_paise || 0 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setToday = () => { setStart(todayIso()); setEnd(todayIso()); };

  return (
    <div className="space-y-5">
      <div className="dashboard-card p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="input-pill py-2" data-testid="jrn-start" />
          <span className="text-slate-400">→</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="input-pill py-2" data-testid="jrn-end" />
          <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="input-pill py-2 min-w-[16rem]" data-testid="jrn-event">
            <option value="">All events</option>
            {events.map((ev) => <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>)}
          </select>
          <button onClick={load} className="btn-primary text-sm" data-testid="jrn-apply"><Search className="w-4 h-4" /> Apply</button>
          <button onClick={setToday} className="btn-ghost text-sm" data-testid="jrn-today">Today only</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="dashboard-card p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><ArrowUp className="w-5 h-5" /></div><div><div className="text-xs text-slate-500 font-bold">TOTAL RECEIVED</div><div className="text-2xl font-display font-black text-emerald-600">₹{(totals.dr / 100).toLocaleString("en-IN")}</div></div></div>
        <div className="dashboard-card p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center"><BookOpen className="w-5 h-5" /></div><div><div className="text-xs text-slate-500 font-bold">ENTRIES</div><div className="text-2xl font-display font-black">{entries.length}</div></div></div>
      </div>

      <div className="dashboard-card p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="jrn-table">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr className="text-left text-[10px] uppercase text-slate-500">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Receipt #</th>
              <th className="px-4 py-3">Received from</th>
              <th className="px-4 py-3">Event</th>
              <th className="px-4 py-3 text-right">Kaimera fee/student</th>
              <th className="px-4 py-3 text-right">Amount received</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              : entries.length === 0 ? <tr><td colSpan={6} className="text-center py-12 text-slate-400">No amounts received in this range.</td></tr>
                : entries.map((e) => (
                  <tr key={e.entry_id} className="border-b border-slate-50" data-testid={`jrn-row-${e.entry_id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{e.date}</td>
                    <td className="px-4 py-3 font-mono text-xs font-bold">{e.receipt_number || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="font-bold">{e.payer_name || "—"}</div>
                      {e.school_name && <div className="text-[10px] text-slate-500">{e.school_name}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{e.event_name || "—"}</td>
                    <td className="px-4 py-3 text-right text-xs font-mono text-slate-500">₹{((e.kaimera_fee_paise || 0) / 100).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">+ ₹{(e.amount_paise / 100).toLocaleString("en-IN")}</span>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =========================================================
// RECEIPTS — list + filters + individual PDF + zip download
// =========================================================
function LiveStatusBadge({ live, isOnline }) {
  if (!isOnline) return <span className="text-[10px] text-slate-300">—</span>;
  if (!live) return <span className="text-[10px] text-slate-400 inline-flex items-center gap-1"><Loader2 className="w-2.5 h-2.5 animate-spin" /> checking…</span>;
  const s = live.status;
  if (s === "captured")   return <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1" title={`Razorpay confirms captured @ ${(live.checked_at || "").slice(11, 16)}`}><CheckCircle2 className="w-2.5 h-2.5" /> CAPTURED</span>;
  if (s === "authorized") return <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1" title="Authorised but not yet captured — reserved funds, review before treating as settled"><AlertTriangle className="w-2.5 h-2.5" /> AUTHORIZED</span>;
  if (s === "failed" || s === "created")  return <span className="text-[10px] font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1" title="Razorpay reports this payment never captured — this receipt should be reconciled/deleted"><XCircle className="w-2.5 h-2.5" /> {s.toUpperCase()}</span>;
  if (s === "refunded")   return <span className="text-[10px] font-bold text-slate-700 bg-slate-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1" title="Refunded via Razorpay"><RotateCcw className="w-2.5 h-2.5" /> REFUNDED</span>;
  if (s === "error")      return <span className="text-[10px] font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1" title={live.error || "Fetch failed"}>⚠︎ ERROR</span>;
  if (s === "n/a")        return <span className="text-[10px] text-slate-300">—</span>;
  return <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full uppercase">{s}</span>;
}

function ReceiptsTab({ events }) {
  const [start, setStart] = useState(todayIso());
  const [end, setEnd] = useState(todayIso());
  const [eventId, setEventId] = useState("");
  const [mode, setMode] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [rescueOpen, setRescueOpen] = useState(false);
  // Per-receipt live Razorpay status, keyed by receipt_id → {status, ok, checked_at}
  const [liveStatus, setLiveStatus] = useState({});
  const [liveLoading, setLiveLoading] = useState(false);
  // Full edit modal — name, email, phone, school AND amount (amount rewrites
  // the journal split server-side so ledgers stay consistent).
  const [editReceipt, setEditReceipt] = useState(null);
  const token = () => localStorage.getItem("munners_token") || "";

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (start) params.append("start", start);
      if (end) params.append("end", end);
      if (eventId) params.append("event_id", eventId);
      if (mode) params.append("mode", mode);
      const r = await api.get(`/accountant/receipts?${params.toString()}`);
      setRows(r.data || []);
      setLiveStatus({});  // reset before the next batch fetch
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Lazy-fetch live Razorpay status for each visible online receipt as soon
  // as the rows land. Split into batches of 25 so a slow Razorpay response
  // doesn't lock the whole grid. Skipped for cash/manual receipts and
  // whenever the accountant navigates away.
  useEffect(() => {
    let cancelled = false;
    const ids = rows.filter((r) => r.payment_mode === "online" && r.payment_id).map((r) => r.receipt_id);
    if (ids.length === 0) return;
    (async () => {
      setLiveLoading(true);
      try {
        for (let i = 0; i < ids.length; i += 25) {
          if (cancelled) return;
          const chunk = ids.slice(i, i + 25);
          try {
            const r = await api.post("/accountant/receipts/live-status", { receipt_ids: chunk });
            if (cancelled) return;
            setLiveStatus((prev) => ({ ...prev, ...r.data }));
          } catch { /* silent — live status is opportunistic */ }
        }
      } finally { if (!cancelled) setLiveLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [rows]);

  const downloadPdf = (r) => {
    window.open(`${BACKEND_URL}/api/accountant/receipts/${r.receipt_id}/pdf?t=${token()}`, "_blank");
  };

  const delReceipt = async (r) => {
    if (!window.confirm(`Delete receipt ${r.receipt_number} for ${r.payer_name} (₹${(r.amount_paise / 100).toLocaleString("en-IN")})?\n\nThis will also reverse the journal entries.`)) return;
    try {
      await api.delete(`/accountant/receipts/${r.receipt_id}`);
      toast.success(`Receipt ${r.receipt_number} deleted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const resendReceipt = async (r) => {
    const defaultTo = r.payer_email || "";
    const to = window.prompt(
      `Resend receipt ${r.receipt_number} to which email?\n\nThe latest PDF will be regenerated (so any renumbering is reflected).`,
      defaultTo,
    );
    if (to === null) return;                // cancelled
    const clean = (to || "").trim();
    if (clean && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) { toast.error("Invalid email address"); return; }
    try {
      const res = await api.post(`/accountant/receipts/${r.receipt_id}/resend`,
        clean && clean !== defaultTo ? { recipient: clean } : {});
      toast.success(`Sent to ${res.data.sent_to}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
  };

  const editPayerName = (r) => setEditReceipt(r);

  const reconcile = async () => {
    if (!window.confirm("Reconcile receipts with Razorpay?\n\nThis calls Razorpay for every receipt tied to an online payment and DELETES any whose live payment status is not 'captured' (i.e. failed / cancelled / never went through). Their journal entries and any orphan school accounts will also be removed. Then numbering is compacted.\n\nRun this once after Feb 2026's capture-guard rollout to purge legacy leaks. Safe to re-run — captured payments are untouched.")) return;
    try {
      const r = await api.post("/accountant/receipts/reconcile-with-gateway");
      const purged = r.data.purged_ids || [];
      toast.success(`Kept ${r.data.kept} · Purged ${r.data.purged}${r.data.fetch_errors ? ` · ${r.data.fetch_errors} fetch errors` : ""}` + (purged.length ? ` — ${purged.slice(0, 3).join(", ")}${purged.length > 3 ? "…" : ""}` : ""));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    }
  };

  const rectify = async () => {
    if (!window.confirm("Rectify receipt numbering?\n\nAll receipts in the current billing period will be renumbered by their issued date/time so the earliest becomes #001, the next #002, and so on — with zero gaps.\n\nSafe & reversible only by re-issuing; PDFs/emails already sent to payers will retain their old numbers. Continue?")) return;
    try {
      const r = await api.post("/accountant/receipts/rectify-numbering");
      toast.success(`Renumbered ${r.data.renumbered} of ${r.data.total_receipts} receipt(s) in period ${r.data.period}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rectify failed");
    }
  };

  const rescueOrphanPayment = () => setRescueOpen(true);

  const downloadZip = () => {
    if (rows.length === 0) { toast.error("No receipts in this range"); return; }
    setZipping(true);
    const params = new URLSearchParams();
    if (start) params.append("start", start);
    if (end) params.append("end", end);
    if (eventId) params.append("event_id", eventId);
    params.append("t", token());
    window.open(`${BACKEND_URL}/api/accountant/receipts/download-zip?${params.toString()}`, "_blank");
    setTimeout(() => setZipping(false), 1500);
  };

  return (
    <div className="space-y-5">
      <div className="dashboard-card p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="input-pill py-2" data-testid="rcp-start" />
          <span className="text-slate-400">→</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="input-pill py-2" data-testid="rcp-end" />
          <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="input-pill py-2 min-w-[14rem]" data-testid="rcp-event">
            <option value="">All events</option>
            {events.map((ev) => <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>)}
          </select>
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="input-pill py-2" data-testid="rcp-mode">
            <option value="">All modes</option>
            <option value="online">Online</option>
            <option value="cash">Cash</option>
          </select>
          <button onClick={load} className="btn-primary text-sm" data-testid="rcp-apply"><Search className="w-4 h-4" /> Apply</button>
          <button onClick={reconcile} className="btn-ghost text-sm inline-flex items-center gap-1 text-rose-700" title="Ask Razorpay for live status; purge any receipt whose payment never actually captured" data-testid="rcp-reconcile">
            <ShieldCheck className="w-4 h-4" /> Reconcile with Razorpay
          </button>
          <button onClick={rectify} className="btn-ghost text-sm inline-flex items-center gap-1" title="Renumber receipts by date/time — closes any gaps" data-testid="rcp-rectify">
            <ListOrdered className="w-4 h-4" /> Rectify numbering
          </button>
          <button onClick={rescueOrphanPayment} className="btn-ghost text-sm inline-flex items-center gap-1 text-emerald-700" title="Paste a Razorpay payment_id to create a missing receipt+journal for a payment that went through on Razorpay but never landed in the ledger (browser closed before /verify-payment fired)" data-testid="rcp-rescue">
            <ShieldCheck className="w-4 h-4" /> Rescue payment
          </button>
          <div className="flex-1" />
          <button onClick={downloadZip} disabled={zipping || rows.length === 0} className="btn-primary text-sm bg-slate-800 hover:bg-slate-900 disabled:opacity-50" data-testid="rcp-zip">
            {zipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download {rows.length} as ZIP
          </button>
        </div>
      </div>

      <div className="dashboard-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr className="text-left text-[10px] uppercase text-slate-500">
              <th className="px-4 py-3">Receipt #</th>
              <th className="px-4 py-3">Payer</th>
              <th className="px-4 py-3">Event</th>
              <th className="px-4 py-3">Mode</th>
              <th className="px-4 py-3">Live status
                {liveLoading && <Loader2 className="inline w-3 h-3 ml-1 animate-spin text-slate-400" />}
              </th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Issued</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={8} className="text-center py-12 text-slate-400">No receipts in this range.</td></tr>
                : rows.map((r) => {
                  const live = liveStatus[r.receipt_id];
                  return (
                  <tr key={r.receipt_id} className={"border-b border-slate-50 hover:bg-slate-50" + (r.suspected_duplicate ? " bg-amber-50/40" : "")} data-testid={`rcp-row-${r.receipt_id}`}>
                    <td className="px-4 py-3 font-mono font-bold">{r.receipt_number}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <div className="font-bold">{r.payer_name}</div>
                        <button
                          onClick={() => editPayerName(r)}
                          className="text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 p-1 rounded"
                          title="Edit receipt (name, email, phone, school, amount)"
                          data-testid={`rcp-edit-name-${r.receipt_id}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        {r.suspected_duplicate && (
                          <span
                            className="text-[9px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5"
                            title="Same payer + event + amount as another receipt within 10 minutes. Review before treating as separate revenue."
                            data-testid={`rcp-dup-${r.receipt_id}`}
                          >
                            <AlertTriangle className="w-2.5 h-2.5" /> POSSIBLE DUPLICATE
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{r.payer_email || r.payer_phone}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{r.event_name}</td>
                    <td className="px-4 py-3">
                      <ModeBadge mode={r.payment_mode} />
                    </td>
                    <td className="px-4 py-3" data-testid={`rcp-live-${r.receipt_id}`}>
                      <LiveStatusBadge live={live} isOnline={r.payment_mode === "online"} />
                    </td>
                    <td className="px-4 py-3 text-right font-bold">₹{(r.amount_paise / 100).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{(r.issued_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => downloadPdf(r)} className="text-emerald-700 hover:bg-emerald-50 p-1.5 rounded" title="Download PDF" data-testid={`rcp-dl-${r.receipt_id}`}>
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => resendReceipt(r)}
                          className="text-sky-700 hover:bg-sky-50 p-1.5 rounded"
                          title={r.last_resent_at ? `Last resent to ${r.last_resent_to || r.payer_email} on ${(r.last_resent_at || "").slice(0, 16).replace("T", " ")}` : "Email the updated PDF to the payer"}
                          data-testid={`rcp-resend-${r.receipt_id}`}
                        >
                          <Send className="w-4 h-4" />
                        </button>
                        <button onClick={() => delReceipt(r)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded" title="Delete receipt" data-testid={`rcp-del-${r.receipt_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
      {rescueOpen && (
        <BulkRescueModal
          onClose={() => setRescueOpen(false)}
          onDone={() => { setRescueOpen(false); load(); }}
        />
      )}
      {editReceipt && (
        <EditReceiptModal
          receipt={editReceipt}
          onClose={() => setEditReceipt(null)}
          onSaved={() => { setEditReceipt(null); load(); }}
        />
      )}
    </div>
  );
}

// =========================================================
// EDIT RECEIPT MODAL — name, email, phone, school and AMOUNT.
// Amount changes rewrite the journal split server-side so the
// school ledger + Kaimera share stay consistent.
// =========================================================
function EditReceiptModal({ receipt, onClose, onSaved }) {
  const [form, setForm] = useState({
    payer_name: receipt.payer_name || "",
    payer_email: receipt.payer_email || "",
    payer_phone: receipt.payer_phone || "",
    school_name: receipt.school_name || "",
    amount: String((receipt.amount_paise || 0) / 100),
    payment_mode: receipt.payment_mode || "cash",
    receipt_date: (receipt.issued_at || "").slice(0, 10),
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.payer_name.trim()) { toast.error("Name cannot be empty"); return; }
    const amountPaise = Math.round(parseFloat(form.amount || "0") * 100);
    if (!amountPaise || amountPaise <= 0) { toast.error("Amount must be > 0"); return; }
    if (form.payer_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.payer_email.trim())) { toast.error("Invalid email"); return; }
    if (!form.receipt_date) { toast.error("Pick a receipt date"); return; }
    setSaving(true);
    try {
      const body = {
        payer_name: form.payer_name.trim(),
        payer_email: form.payer_email.trim(),
        payer_phone: form.payer_phone.trim(),
        school_name: form.school_name.trim(),
      };
      if (amountPaise !== receipt.amount_paise) body.amount_paise = amountPaise;
      if (form.payment_mode !== (receipt.payment_mode || "cash")) body.payment_mode = form.payment_mode;
      const dateChanged = form.receipt_date !== (receipt.issued_at || "").slice(0, 10);
      if (dateChanged) body.receipt_date = form.receipt_date;
      const r = await api.patch(`/accountant/receipts/${receipt.receipt_id}`, body);
      const renumbered = r.data?.receipt_number && r.data.receipt_number !== receipt.receipt_number;
      toast.success(`Receipt updated${renumbered ? ` — renumbered by date to ${r.data.receipt_number}` : ""}${body.amount_paise || body.payment_mode || dateChanged ? " (journal rewritten)" : ""}`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-3" data-testid="edit-receipt-modal">
        <div>
          <h4 className="font-display font-bold text-lg">Edit receipt <span className="font-mono text-orange-600">{receipt.receipt_number}</span></h4>
          <p className="text-xs text-slate-500">Name changes cascade into the journal narration; amount changes rewrite the Kaimera/school split. Hit Resend afterwards to email the corrected PDF.</p>
        </div>
        <div className="space-y-2.5">
          <input placeholder="Payer name *" className="input-pill w-full" value={form.payer_name} onChange={(e) => setForm({ ...form, payer_name: e.target.value })} data-testid="edit-rcp-name" />
          <input placeholder="Email" type="email" className="input-pill w-full" value={form.payer_email} onChange={(e) => setForm({ ...form, payer_email: e.target.value })} data-testid="edit-rcp-email" />
          <input placeholder="Phone" className="input-pill w-full" value={form.payer_phone} onChange={(e) => setForm({ ...form, payer_phone: e.target.value })} data-testid="edit-rcp-phone" />
          <input placeholder="School" className="input-pill w-full" value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} data-testid="edit-rcp-school" />
          <div>
            <label className="text-xs text-slate-500">Amount (₹) — rewrites journal split if changed</label>
            <input placeholder="Amount (₹) *" type="number" step="0.01" className="input-pill w-full mt-1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="edit-rcp-amount" />
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className="text-xs text-slate-500">Payment type</label>
              <select className="input-pill w-full mt-1" value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} data-testid="edit-rcp-mode">
                {MODE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Receipt date</label>
              <input type="date" max={todayLocal()} className="input-pill w-full mt-1" value={form.receipt_date} onChange={(e) => setForm({ ...form, receipt_date: e.target.value })} data-testid="edit-rcp-date" />
            </div>
          </div>
          {form.receipt_date !== (receipt.issued_at || "").slice(0, 10) && (
            <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5" data-testid="edit-rcp-renumber-note">Changing the date renumbers receipts in this period so numbers stay in date order.</p>
          )}
        </div>
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" onClick={onClose} className="btn-ghost" data-testid="edit-rcp-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="edit-rcp-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

// =========================================================
// BULK RESCUE MODAL — paste payment_ids or upload CSV, then
// stream per-row status as each rescue completes. Backed by
// `POST /api/accountant/reconcile-payment` per row so we get
// naturally live UI without long-polling. Max 100 rows.
// =========================================================
function BulkRescueModal({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const [rows, setRows] = useState([]);           // {payment_id, status: pending|ok|err, receipt_number, payer_name, error}
  const [doneCount, setDoneCount] = useState(0);

  const parseIds = () => {
    // Extract every `pay_...` token from the textarea / uploaded CSV blob.
    // We DON'T require a strict format — the accountant might paste rows
    // like `pay_XXXX, Rs 2500, name@x.com` and we still want to pull the
    // ID out. Dedupe while preserving order.
    const found = (text.match(/pay_[a-zA-Z0-9]+/g) || []);
    const seen = new Set();
    return found.filter((p) => { if (seen.has(p)) return false; seen.add(p); return true; });
  };

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const txt = await file.text();
      setText((t) => (t ? t + "\n" : "") + txt);
      toast.success(`Loaded ${file.name}`);
    } catch { toast.error("Could not read file"); }
  };

  const start = async () => {
    const ids = parseIds();
    if (ids.length === 0) { toast.error("No `pay_XXX` IDs detected in the input"); return; }
    if (ids.length > 100) { toast.error("Max 100 payment IDs per batch"); return; }
    // Seed the visible rows so the user sees the full list up front.
    setRows(ids.map((pid) => ({ payment_id: pid, status: "pending" })));
    setDoneCount(0);
    setRunning(true);
    // Process one at a time so we can update status per-row (also friendly to
    // Razorpay rate limits). Small delay between rows keeps the UI responsive.
    for (let i = 0; i < ids.length; i++) {
      const pid = ids[i];
      try {
        const r = await api.post("/accountant/reconcile-payment", { payment_id: pid });
        setRows((prev) => {
          const copy = [...prev];
          copy[i] = {
            payment_id: pid,
            status: "ok",
            receipt_number: r.data.receipt_number,
            payer_name: r.data.payer_name,
            created_submission: r.data.created_submission,
          };
          return copy;
        });
      } catch (e) {
        setRows((prev) => {
          const copy = [...prev];
          copy[i] = { payment_id: pid, status: "err", error: e?.response?.data?.detail || "Failed" };
          return copy;
        });
      }
      setDoneCount((c) => c + 1);
    }
    setRunning(false);
    toast.success("Bulk rescue complete");
  };

  const total = rows.length;
  const okCount = rows.filter((r) => r.status === "ok").length;
  const errCount = rows.filter((r) => r.status === "err").length;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="rescue-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase font-bold text-emerald-600">Bulk rescue</div>
            <h3 className="font-display font-black text-xl">Import Razorpay payment IDs</h3>
            <div className="text-xs text-slate-500 mt-1">Paste up to 100 payment IDs (one per line) or upload a CSV. Each ID is fetched live from Razorpay — captured payments become receipts + journal entries. Rows already reconciled return their existing receipt (safe to re-run).</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full text-slate-500 hover:bg-slate-100" data-testid="rescue-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {rows.length === 0 && (
            <>
              <div>
                <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Payment IDs</div>
                <textarea
                  rows={10}
                  className="input-pill resize-none font-mono text-sm w-full"
                  placeholder={"pay_ABC123XYZ\npay_DEF456UVW\npay_GHI789RST"}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  data-testid="rescue-textarea"
                />
                <div className="text-xs text-slate-400 mt-1">Paste anything — commas, extra columns, comments. We&apos;ll extract every <code className="font-mono">pay_XXXX</code> token.</div>
              </div>
              <div className="flex items-center gap-2">
                <label className="btn-ghost cursor-pointer inline-flex items-center gap-1.5 text-sm">
                  <Upload className="w-4 h-4" /> Upload CSV / TXT
                  <input type="file" accept=".csv,.txt,text/csv,text/plain" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="rescue-file" />
                </label>
                <div className="text-xs text-slate-400">or paste above</div>
                <div className="flex-1" />
                <button
                  onClick={start}
                  className="btn-primary text-sm inline-flex items-center gap-1.5"
                  disabled={running}
                  data-testid="rescue-start"
                >
                  <ShieldCheck className="w-4 h-4" /> Rescue {parseIds().length || ""} payment{parseIds().length === 1 ? "" : "s"}
                </button>
              </div>
            </>
          )}
          {rows.length > 0 && (
            <>
              <div className="flex items-center gap-3 text-sm">
                <div className="font-bold">{doneCount}/{total} processed</div>
                <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> {okCount}</span>
                <span className="text-rose-700 inline-flex items-center gap-1"><XCircle className="w-4 h-4" /> {errCount}</span>
                {running && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
              </div>
              <div className="border border-slate-100 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold">
                    <tr>
                      <th className="text-left px-3 py-2">Payment ID</th>
                      <th className="text-left px-3 py-2">Result</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((r) => (
                      <tr key={r.payment_id} data-testid={`rescue-row-${r.payment_id}`}>
                        <td className="px-3 py-2 font-mono text-xs">{r.payment_id}</td>
                        <td className="px-3 py-2">
                          {r.status === "pending" && <span className="inline-flex items-center gap-1 text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> Fetching from Razorpay…</span>}
                          {r.status === "ok" && (
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              <span className="font-bold">{r.receipt_number}</span>
                              <span className="text-slate-500">·</span>
                              <span>{r.payer_name}</span>
                              {r.created_submission && <span className="text-[9px] font-bold bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full">NEW SUBMISSION</span>}
                            </span>
                          )}
                          {r.status === "err" && (
                            <span className="inline-flex items-center gap-1 text-rose-700 text-xs">
                              <XCircle className="w-3.5 h-3.5" /> {r.error}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!running && (
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button onClick={() => { setRows([]); setDoneCount(0); setText(""); }} className="btn-ghost text-sm" data-testid="rescue-reset">Rescue another batch</button>
                  <button onClick={onDone} className="btn-primary text-sm" data-testid="rescue-done">Done · Refresh receipts</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// =========================================================
// ADD RECEIPT — manual cash/online entry
// =========================================================
function AddReceiptTab({ events, onCreated }) {
  const blankForm = () => ({ name: "", email: "", phone: "", event_id: "", school_name: "", payment_mode: "cash", amount: "", reference: "", receipt_date: todayLocal() });
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [schools, setSchools] = useState([]);
  const [customSchool, setCustomSchool] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  // Bulk mode — one receipt covering many students, each with their own
  // name + fee. ONE PDF + ONE email to the payer.
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkRows, setBulkRows] = useState([{ name: "", fee: "" }]);

  useEffect(() => {
    if (!form.event_id) { setSchools([]); return; }
    (async () => {
      try {
        const r = await api.get(`/accountant/events/${form.event_id}/receipt-context`);
        setSchools(r.data.schools || []);
        // Auto-fill amount from event's delegate_fee (accountant can override)
        const fee = r.data.delegate_fee || 0;
        setForm((f) => ({ ...f, school_name: "", amount: fee > 0 ? String(fee) : f.amount }));
        // In bulk mode, pre-fill the per-delegate fee with the event fee
        if (fee > 0) setBulkRows((rows) => rows.map((r2) => ({ ...r2, fee: r2.fee || String(fee) })));
      } catch {
        setSchools([]);
        setForm((f) => ({ ...f, school_name: "" }));
      }
    })();
    setShowCustom(false);
    setCustomSchool("");
  }, [form.event_id]);

  const save = async (e) => {
    e.preventDefault();
    const school_name = (showCustom ? customSchool : form.school_name).trim();
    setSaving(true);
    try {
      if (bulkMode) {
        const delegates = bulkRows
          .filter((r) => r.name.trim() && r.fee)
          .map((r) => ({ name: r.name.trim(), fee_paise: Math.round(parseFloat(r.fee) * 100) }));
        if (!form.name.trim() || !form.event_id || delegates.length === 0) { toast.error("Payer, event and at least one delegate (name + fee) are required"); setSaving(false); return; }
        const r = await api.post("/accountant/receipts/manual-bulk", {
          payer_name: form.name.trim(), payer_email: form.email.trim(), payer_phone: form.phone.trim(),
          event_id: form.event_id, school_name,
          payment_mode: form.payment_mode,
          receipt_date: form.receipt_date,
          reference: form.reference.trim(),
          delegates,
        });
        toast.success(`Bulk receipt ${r.data.receipt_number} issued for ${delegates.length} delegate(s) — one email sent`);
        setBulkRows([{ name: "", fee: "" }]);
      } else {
        if (!form.name.trim() || !form.event_id || !form.amount) { toast.error("Name, event and amount are required"); setSaving(false); return; }
        const r = await api.post("/accountant/receipts/manual", {
          name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
          event_id: form.event_id, school_name,
          payment_mode: form.payment_mode,
          receipt_date: form.receipt_date,
          amount_paise: Math.round(parseFloat(form.amount) * 100),
          reference: form.reference.trim(),
        });
        toast.success(`Receipt ${r.data.receipt_number} issued`);
      }
      setForm(blankForm());
      setCustomSchool(""); setShowCustom(false);
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const bulkTotal = bulkRows.reduce((s, r) => s + (parseFloat(r.fee) || 0), 0);

  return (
    <form onSubmit={save} className="dashboard-card p-6 space-y-4 max-w-2xl" data-testid="add-receipt-form">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h4 className="font-display font-black text-lg">{bulkMode ? "Add BULK receipt (many students, one receipt)" : "Add manual receipt"}</h4>
          <p className="text-sm text-slate-500">
            {bulkMode
              ? "Issue ONE receipt covering multiple students — each with their own name + fee. A single PDF (with the full breakdown) and a single email go to the payer."
              : "Issue a receipt for a cash / offline / re-issued payment. If a school is picked, this receipt joins the school account ledger and Kaimera's per-delegate fee is auto-carved out."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setBulkMode((v) => !v)}
          className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${bulkMode ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"}`}
          data-testid="bulk-mode-toggle"
        >
          {bulkMode ? "✓ Bulk mode ON" : "Switch to bulk mode"}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input placeholder={bulkMode ? "Payer / school contact name *" : "Payer name *"} className="input-pill" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-name" />
        <input placeholder="Email" type="email" className="input-pill" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-email" />
        <input placeholder="Phone" className="input-pill" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="add-phone" />
        <select value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })} className="input-pill" data-testid="add-event">
          <option value="">Select event *</option>
          {events.map((ev) => <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>)}
        </select>
        <div className="md:col-span-2">
          <label className="text-xs text-slate-500">School (opens/updates school-account ledger)</label>
          {!showCustom && schools.length > 0 ? (
            <div className="flex gap-2 mt-1">
              <select value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} className="input-pill flex-1" data-testid="add-school" disabled={!form.event_id}>
                <option value="">— No school (no ledger entry) —</option>
                {schools.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button type="button" onClick={() => setShowCustom(true)} className="btn-ghost text-xs" data-testid="add-school-custom">+ Other</button>
            </div>
          ) : (
            <div className="flex gap-2 mt-1">
              <input
                placeholder={form.event_id ? "Type the school name (no allotments yet)" : "Pick an event first"}
                value={showCustom ? customSchool : form.school_name}
                onChange={(e) => (showCustom ? setCustomSchool(e.target.value) : setForm({ ...form, school_name: e.target.value }))}
                className="input-pill flex-1"
                disabled={!form.event_id}
                data-testid="add-school-input"
              />
              {schools.length > 0 && (
                <button type="button" onClick={() => { setShowCustom(false); setCustomSchool(""); }} className="btn-ghost text-xs">Pick from list</button>
              )}
            </div>
          )}
        </div>
        <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="input-pill" data-testid="add-mode">
          {MODE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {!bulkMode && (
          <input placeholder="Amount (₹) *" type="number" step="0.01" className="input-pill" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="add-amount" title="Auto-filled from the event's per-delegate fee — override if needed." />
        )}
        <div>
          <label className="text-xs text-slate-500">Receipt date *</label>
          <input type="date" required max={todayLocal()} className="input-pill w-full mt-1" value={form.receipt_date} onChange={(e) => setForm({ ...form, receipt_date: e.target.value })} data-testid="add-date" />
        </div>
        <input placeholder="Reference / UPI ID / cheque # (optional)" className="input-pill md:col-span-2" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="add-ref" />
      </div>

      {bulkMode && (
        <div className="rounded-xl border border-orange-200 bg-orange-50/40 p-4 space-y-2" data-testid="bulk-delegate-editor">
          <div className="text-xs font-bold text-orange-800">Delegates covered by this receipt</div>
          {bulkRows.map((row, i) => (
            <div key={i} className="grid grid-cols-[1.5rem_1fr_7rem_1.75rem] gap-2 items-center">
              <span className="text-xs text-slate-400 text-right">{i + 1}.</span>
              <input placeholder="Student name *" className="input-pill py-2 text-sm w-full" value={row.name}
                onChange={(e) => setBulkRows((rows) => rows.map((r2, j) => j === i ? { ...r2, name: e.target.value } : r2))}
                data-testid={`bulk-name-${i}`} />
              <input placeholder="Fee (₹) *" type="number" step="0.01" className="input-pill py-2 text-sm w-full" value={row.fee}
                onChange={(e) => setBulkRows((rows) => rows.map((r2, j) => j === i ? { ...r2, fee: e.target.value } : r2))}
                data-testid={`bulk-fee-${i}`} />
              <button type="button" onClick={() => setBulkRows((rows) => rows.filter((_, j) => j !== i))}
                className="text-rose-500 hover:bg-rose-50 p-1.5 rounded text-center" disabled={bulkRows.length === 1}
                title="Remove row" data-testid={`bulk-del-${i}`}>×</button>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <button type="button" onClick={() => setBulkRows((rows) => [...rows, { name: "", fee: form.amount || "" }])}
              className="btn-ghost text-xs py-1.5" data-testid="bulk-add-row">+ Add student</button>
            <div className="text-sm font-bold text-slate-700" data-testid="bulk-total">
              Total: ₹{bulkTotal.toLocaleString("en-IN")} · {bulkRows.filter((r) => r.name.trim()).length} delegate(s)
            </div>
          </div>
        </div>
      )}

      <button type="submit" disabled={saving} className="btn-primary" data-testid="add-submit">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {saving ? "Issuing…" : bulkMode ? `Issue bulk receipt (₹${bulkTotal.toLocaleString("en-IN")})` : "Issue receipt"}
      </button>
    </form>
  );
}

// =========================================================
// SCHOOLS — search list + open ledger in new tab
// =========================================================
function SchoolsTab({ events }) {
  const [search, setSearch] = useState("");
  const [eventId, setEventId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reconcileAcc, setReconcileAcc] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (eventId) params.append("event_id", eventId);
      const r = await api.get(`/accountant/school-accounts?${params.toString()}`);
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const backfill = async () => {
    if (!window.confirm("Retroactively link every existing receipt to a school account (based on each event's host school). Safe to run multiple times.")) return;
    try {
      const r = await api.post("/accountant/receipts/backfill-school-accounts");
      toast.success(`Backfill done — linked ${r.data.linked} · already ok ${r.data.already_linked} · no school ${r.data.skipped_no_school} · no event fee ${r.data.skipped_no_event_fee}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
  };

  const openLedger = (a) => window.open(`/dashboard/accountant/school/${a.school_account_id}`, "_blank");

  const mergeAccount = async (a, e) => {
    e.stopPropagation();
    const others = rows.filter((r) => r.school_account_id !== a.school_account_id && r.event_id === a.event_id);
    if (!others.length) { toast.error("No other school account in this event to merge into"); return; }
    const menu = others.map((o, i) => `${i + 1}. ${o.school_name}`).join("\n");
    const pick = window.prompt(
      `Merge "${a.school_name}" INTO another account?\n\nAll its receipts, journal entries and payouts will move, and "${a.school_name}" will be deleted.\n\nPick the account to KEEP:\n${menu}\n\nEnter 1-${others.length}:`,
      "1",
    );
    if (pick === null) return;
    const idx = parseInt(pick, 10) - 1;
    if (isNaN(idx) || idx < 0 || idx >= others.length) { toast.error("Invalid choice"); return; }
    const keep = others[idx];
    if (!window.confirm(`Merge "${a.school_name}" into "${keep.school_name}"? This cannot be undone.`)) return;
    try {
      const r = await api.post("/accountant/school-accounts/merge", { keep_id: keep.school_account_id, merge_id: a.school_account_id });
      toast.success(`Merged — ${r.data.receipts_moved} receipt(s), ${r.data.journal_legs_moved} journal leg(s) moved into "${keep.school_name}"`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Merge failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="dashboard-card p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[16rem]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input placeholder="Search by school name…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="input-pill pl-9" data-testid="schools-search" />
          </div>
          <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="input-pill py-2 min-w-[14rem]" data-testid="schools-event">
            <option value="">All events</option>
            {events.map((ev) => <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>)}
          </select>
          <button onClick={load} className="btn-primary text-sm" data-testid="schools-apply"><Search className="w-4 h-4" /> Apply</button>
          <button onClick={backfill} className="btn-ghost text-sm" data-testid="schools-backfill">Backfill from receipts</button>
        </div>
      </div>

      <div className="dashboard-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr className="text-left text-[10px] uppercase text-slate-500">
              <th className="px-4 py-3">School · Event</th>
              <th className="px-4 py-3 text-right">Students paid</th>
              <th className="px-4 py-3 text-right">Collected</th>
              <th className="px-4 py-3 text-right">Kaimera share</th>
              <th className="px-4 py-3 text-right">Owed to school</th>
              <th className="px-4 py-3 text-right">Received from school</th>
              <th className="px-4 py-3 text-right">Paid out</th>
              <th className="px-4 py-3 text-right">Balance due</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={9} className="text-center py-12 text-slate-400">No school accounts yet. Create one by adding a receipt with a school on <b>Add receipt</b>.</td></tr>
                : rows.map((a) => (
                  <tr key={a.school_account_id} onClick={() => openLedger(a)} className="border-b border-slate-50 cursor-pointer hover:bg-emerald-50/40" data-testid={`school-row-${a.school_account_id}`}>
                    <td className="px-4 py-3"><div className="font-bold">{a.school_name}</div><div className="text-xs text-slate-500">{a.event_name} · fee/student ₹{a.kaimera_fee_per_delegate_paise / 100}</div></td>
                    <td className="px-4 py-3 text-right"><span className="font-bold">{a.students_covered_by_payouts}</span> / {a.students_paid_count}</td>
                    <td className="px-4 py-3 text-right font-mono">₹{(a.total_collected_paise / 100).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right font-mono">₹{(a.kaimera_retained_paise / 100).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right font-mono">₹{(a.school_owed_paise / 100).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right font-mono text-sky-700" data-testid={`school-received-${a.school_account_id}`}>₹{((a.received_from_school_paise || 0) / 100).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right font-mono">₹{(a.school_paid_paise / 100).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right"><span className={`font-mono font-bold ${a.school_balance_paise > 0 ? "text-rose-600" : "text-emerald-600"}`}>₹{(a.school_balance_paise / 100).toLocaleString("en-IN")}</span></td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); setReconcileAcc(a); }}
                        className="text-[10px] font-bold text-slate-500 hover:text-orange-600 hover:bg-orange-50 rounded-full px-2 py-1 border border-slate-200 hover:border-orange-200 mr-2"
                        title="Find mismatches: allotted delegates with no receipt, and receipts with no allotment"
                        data-testid={`school-reconcile-${a.school_account_id}`}
                      >Reconcile</button>
                      <button
                        onClick={(e) => mergeAccount(a, e)}
                        className="text-[10px] font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-full px-2 py-1 border border-slate-200 hover:border-rose-200 mr-2"
                        title="Merge this account into another (fixes duplicate school entries like 'GPS' vs 'Gobindgarh Public School')"
                        data-testid={`school-merge-${a.school_account_id}`}
                      >Merge</button>
                      <ExternalLink className="w-4 h-4 text-slate-400 inline" />
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      {reconcileAcc && (
        <ReconcileModal
          account={reconcileAcc}
          onClose={() => setReconcileAcc(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// =========================================================
// RECONCILE — per-school mismatch finder. Two groups:
//  A. Allotted but NO receipt  → one-click "Issue receipt"
//  B. Receipt but NO allotment → link to the event's Allotment tab
// =========================================================
function ReconcileModal({ account, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/accountant/school-accounts/${account.school_account_id}/reconciliation`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); setData({ allotted_without_receipt: [], receipt_without_allotment: [] }); }
  };
  useEffect(() => { load(); }, []);

  const issueReceipt = async (a) => {
    const fee = data?.delegate_fee || 0;
    const amount = fee > 0 ? fee : parseFloat(window.prompt(`Amount (₹) for ${a.student_name}'s receipt:`, "") || "0");
    if (!amount || amount <= 0) { toast.error("Amount required"); return; }
    setBusy(a.allotment_id);
    try {
      const r = await api.post("/accountant/receipts/manual", {
        name: a.student_name, email: a.student_email || "",
        event_id: data.event_id, school_name: data.school_name,
        payment_mode: "cash", amount_paise: Math.round(amount * 100),
      });
      toast.success(`Receipt ${r.data.receipt_number} issued for ${a.student_name}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Issue failed"); }
    finally { setBusy(""); }
  };

  const linkReceipt = async (a) => {
    setBusy(a.allotment_id);
    try {
      await api.patch(`/accountant/receipts/${a.receipt_id}`, { school_account_id: account.school_account_id });
      toast.success(`Receipt ${a.receipt_number} linked to ${account.school_name}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
    finally { setBusy(""); }
  };

  const missingReceipt = data?.allotted_without_receipt || [];
  const notLinked = data?.allotted_receipt_not_linked || [];
  const missingAllot = data?.receipt_without_allotment || [];
  const clean = data && missingReceipt.length === 0 && notLinked.length === 0 && missingAllot.length === 0;

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="reconcile-modal">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-display font-bold text-lg">Reconcile — {account.school_name}</h4>
          <button onClick={onClose} className="btn-ghost text-xs py-1" data-testid="reconcile-close">Close</button>
        </div>
        {data && (
          <p className="text-xs text-slate-500 mb-4">
            {data.allotted_count} allotted · {data.receipt_count} receipt(s) · event: {data.event_name}
          </p>
        )}
        {!data ? (
          <div className="text-center py-8 text-slate-400 text-sm">Scanning…</div>
        ) : clean ? (
          <div className="text-center py-8 text-emerald-600 font-semibold text-sm" data-testid="reconcile-clean">✓ No mismatches — every allotment has a linked receipt and every receipt is allotted.</div>
        ) : (
          <div className="space-y-5">
            {/* Genuinely missing receipts — no receipt exists anywhere in
                this event for these allotted delegates. */}
            <div>
              <div className="text-xs font-bold text-rose-700 mb-2" data-testid="reconcile-missing-receipt-count">
                ⚠ Allotted but NO receipt exists ({missingReceipt.length}) — receipts are missing for these
              </div>
              {missingReceipt.length === 0 ? <div className="text-xs text-slate-400">None.</div> : (
                <div className="space-y-1.5">
                  {missingReceipt.map((a) => (
                    <div key={a.allotment_id} className="flex items-center gap-2 text-sm bg-rose-50/50 border border-rose-100 rounded-lg px-3 py-2" data-testid={`reconcile-no-receipt-${a.allotment_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{a.student_name}</div>
                        <div className="text-xs text-slate-500 truncate">{a.committee} · {a.portfolio}{a.paid ? " · marked paid" : " · unpaid"}</div>
                      </div>
                      <button onClick={() => issueReceipt(a)} disabled={busy === a.allotment_id}
                        className="text-[10px] font-bold text-white bg-orange-500 hover:bg-orange-600 rounded-full px-2.5 py-1 shrink-0 disabled:opacity-50"
                        data-testid={`reconcile-issue-${a.allotment_id}`}>
                        {busy === a.allotment_id ? "Issuing…" : "Issue receipt"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Receipt exists but isn't linked to THIS school account —
                one click links it so the ledger + counts align. */}
            <div>
              <div className="text-xs font-bold text-amber-700 mb-2" data-testid="reconcile-not-linked-count">
                ⚠ Receipt exists but not linked to this school ({notLinked.length}) — receipts exist, just mislinked
              </div>
              {notLinked.length === 0 ? <div className="text-xs text-slate-400">None.</div> : (
                <div className="space-y-1.5">
                  {notLinked.map((a) => (
                    <div key={a.allotment_id} className="flex items-center gap-2 text-sm bg-amber-50/50 border border-amber-100 rounded-lg px-3 py-2" data-testid={`reconcile-not-linked-${a.allotment_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{a.student_name} <span className="font-mono text-xs text-slate-400">{a.receipt_number}</span></div>
                        <div className="text-xs text-slate-500 truncate">{a.committee} · {a.portfolio} · ₹{((a.amount_paise || 0) / 100).toLocaleString("en-IN")}</div>
                      </div>
                      <button onClick={() => linkReceipt(a)} disabled={busy === a.allotment_id}
                        className="text-[10px] font-bold text-white bg-amber-500 hover:bg-amber-600 rounded-full px-2.5 py-1 shrink-0 disabled:opacity-50"
                        data-testid={`reconcile-link-${a.allotment_id}`}>
                        {busy === a.allotment_id ? "Linking…" : "Link here"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs font-bold text-sky-700 mb-2" data-testid="reconcile-missing-allot-count">
                ⚠ Receipt but NO allotment ({missingAllot.length})
              </div>
              {missingAllot.length === 0 ? <div className="text-xs text-slate-400">None.</div> : (
                <div className="space-y-1.5">
                  {missingAllot.map((r) => (
                    <div key={r.receipt_id} className="flex items-center gap-2 text-sm bg-sky-50/50 border border-sky-100 rounded-lg px-3 py-2" data-testid={`reconcile-no-allot-${r.receipt_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{r.payer_name} <span className="font-mono text-xs text-slate-400">{r.receipt_number}</span></div>
                        <div className="text-xs text-slate-500 truncate">
                          ₹{(r.amount_paise / 100).toLocaleString("en-IN")}
                          {r.unallotted_delegates?.length ? ` · not allotted: ${r.unallotted_delegates.slice(0, 3).join(", ")}${r.unallotted_delegates.length > 3 ? ` +${r.unallotted_delegates.length - 3} more` : ""}` : ""}
                        </div>
                      </div>
                      <a href={`/dashboard/events/${data.event_id}`} target="_blank" rel="noreferrer"
                        className="text-[10px] font-bold text-white bg-sky-500 hover:bg-sky-600 rounded-full px-2.5 py-1 shrink-0"
                        data-testid={`reconcile-goto-allot-${r.receipt_id}`}>
                        Allot them →
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// =========================================================
// TEMPLATES — 10 receipt presets + 5 statement presets, activate + edit HTML
// =========================================================
function TemplatesTab() {
  const [kind, setKind] = useState("receipt"); // "receipt" | "statement"
  return (
    <div className="space-y-4">
      <div className="flex gap-2" data-testid="tpl-kind-tabs">
        <button onClick={() => setKind("receipt")} className={`px-4 py-2 rounded-full text-sm font-bold border ${kind === "receipt" ? "bg-emerald-500 text-white border-emerald-500" : "bg-white border-slate-200 hover:border-emerald-300"}`} data-testid="tpl-kind-receipt">Receipt templates</button>
        <button onClick={() => setKind("statement")} className={`px-4 py-2 rounded-full text-sm font-bold border ${kind === "statement" ? "bg-emerald-500 text-white border-emerald-500" : "bg-white border-slate-200 hover:border-emerald-300"}`} data-testid="tpl-kind-statement">Statement templates</button>
      </div>
      {kind === "receipt" ? <ReceiptTemplatesPanel /> : <StatementTemplatesPanel />}
    </div>
  );
}

function ReceiptTemplatesPanel() {
  return <TemplateEditor
    listUrl="/accountant/templates"
    updateUrl={(key) => `/accountant/templates/${key}`}
    resetUrl={(key) => `/accountant/templates/${key}/reset`}
    activeKeyField="active_template_key"
    kindLabel="receipt"
    previewSubs={(settings) => ({
      "{{LOGO_TAG}}": settings?.logo_url ? `<img src="${settings.logo_url}" style="height:44px;margin-bottom:6px"/>` : "",
      "{{BILLED_FROM_NAME}}": settings?.billed_from_name || "Your Company",
      "{{ADDRESS}}": settings?.address || "123 Sample St, Delhi",
      "{{EMAIL}}": settings?.email || "info@example.com",
      "{{CONTACT}}": settings?.contact || "+91 90000 00000",
      "{{RECEIPT_NUMBER}}": `${settings?.prefix || "KL"}/${settings?.billing_period || "25-26"}/001`,
      "{{ISSUED_AT}}": new Date().toISOString().slice(0, 19).replace("T", " "),
      "{{PAYER_NAME}}": "Sanyam Jain",
      "{{PAYER_EMAIL}}": "sanyam@example.com",
      "{{PAYER_PHONE}}": "+91 98765 43210",
      "{{EVENT_NAME}}": "KAIMERA MUN 2026",
      "{{PAYMENT_MODE_LABEL}}": "Online",
      "{{PAYMENT_REF}}": "pay_ABC123",
      "{{AMOUNT}}": "3,200",
      "{{AMOUNT_WORDS}}": "Three Thousand Two Hundred",
    })}
  />;
}

function StatementTemplatesPanel() {
  return <TemplateEditor
    listUrl="/accountant/statement-templates"
    updateUrl={(key) => `/accountant/statement-templates/${key}`}
    resetUrl={(key) => `/accountant/statement-templates/${key}/reset`}
    activeKeyField="active_statement_template_key"
    kindLabel="statement"
    previewSubs={(settings) => ({
      "{{LOGO_TAG}}": settings?.logo_url ? `<img src="${settings.logo_url}" style="height:44px;margin-bottom:6px"/>` : "",
      "{{BILLED_FROM_NAME}}": settings?.billed_from_name || "Kaimera Learning",
      "{{ADDRESS}}": settings?.address || "123 Sample St, Delhi",
      "{{EMAIL}}": settings?.email || "info@example.com",
      "{{CONTACT}}": settings?.contact || "+91 90000 00000",
      "{{SCHOOL_NAME}}": "Career Academy",
      "{{EVENT_NAME}}": "CA MUN 2026",
      "{{GENERATED_AT}}": new Date().toISOString().slice(0, 19).replace("T", " "),
      "{{TOTAL_COLLECTED}}": "Rs. 20,000",
      "{{KAIMERA_SHARE}}": "Rs. 8,000",
      "{{SCHOOL_OWED}}": "Rs. 12,000",
      "{{SCHOOL_PAID}}": "Rs. 4,800",
      "{{SCHOOL_BALANCE}}": "Rs. 7,200",
      "{{STUDENTS_PAID}}": "10",
      "{{STUDENTS_COVERED}}": "4",
      "{{STUDENTS_COUNT}}": "10",
      "{{PAYOUTS_COUNT}}": "1",
      "{{STUDENTS_TABLE}}": "<table width='100%' cellpadding='6' style='border:1px solid #e2e8f0;font-size:9pt'><tr style='background:#f1f5f9;font-weight:bold'><td>#</td><td>Receipt</td><td>Student</td><td style='text-align:right'>Amount</td><td style='text-align:right'>Kaimera share</td><td style='text-align:right'>School share</td></tr><tr><td>1</td><td>KL/25-26/001</td><td>Sample Student</td><td style='text-align:right'>Rs. 2,000</td><td style='text-align:right'>Rs. 800</td><td style='text-align:right'>Rs. 1,200</td></tr></table>",
      "{{PAYOUTS_TABLE}}": "<table width='100%' cellpadding='6' style='border:1px solid #e2e8f0;font-size:9pt'><tr style='background:#f1f5f9;font-weight:bold'><td>Date</td><td>Mode</td><td>Reference</td><td style='text-align:right'>Students</td><td style='text-align:right'>Amount</td></tr><tr><td>2026-07-15</td><td>bank_transfer</td><td>UTR-XYZ-001</td><td style='text-align:right'>4</td><td style='text-align:right'>Rs. 4,800</td></tr></table>",
    })}
  />;
}

function TemplateEditor({ listUrl, updateUrl, resetUrl, activeKeyField, kindLabel, previewSubs }) {
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [tpl, st] = await Promise.all([api.get(listUrl), api.get("/accountant/settings")]);
    setRows(tpl.data); setSettings(st.data);
    if (tpl.data.length) setActive((prev) => prev || tpl.data[0]);
  };
  useEffect(() => { setActive(null); load(); }, [listUrl]);

  const save = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await api.put(updateUrl(active.template_key), { name: active.name, html_body: active.html_body });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const activate = async () => {
    if (!active) return;
    try {
      await api.put("/accountant/settings", { [activeKeyField]: active.template_key });
      toast.success(`"${active.name}" is now the default ${kindLabel} template`);
      const st = await api.get("/accountant/settings"); setSettings(st.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Activate failed"); }
  };

  const reset = async () => {
    if (!active) return;
    if (!window.confirm(`Reset "${active.name}" to the shipped default? Your edits will be lost.`)) return;
    try {
      const r = await api.post(resetUrl(active.template_key));
      setActive(r.data); load();
      toast.success("Reset to default");
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  if (!active) return <div className="p-12 text-slate-400 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  let previewHtml = active.html_body || "";
  const subs = previewSubs(settings);
  Object.entries(subs).forEach(([k, v]) => { previewHtml = previewHtml.split(k).join(String(v)); });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
      <div className="dashboard-card p-3 h-fit" data-testid="tpl-list">
        {rows.map((t) => (
          <button key={t.template_key} onClick={() => setActive(t)} className={`w-full text-left p-3 rounded-xl mb-1 text-sm flex items-center gap-2 ${active.template_key === t.template_key ? "bg-emerald-50 text-emerald-700" : "hover:bg-slate-50"}`} data-testid={`tpl-item-${t.template_key}`}>
            <Palette className="w-4 h-4" />
            <span className="flex-1 truncate">{t.name}</span>
            {settings?.[activeKeyField] === t.template_key && <span className="text-[9px] font-bold text-white bg-emerald-500 px-1.5 py-0.5 rounded-full">ACTIVE</span>}
          </button>
        ))}
      </div>

      <div className="lg:col-span-3 space-y-5">
        <div className="dashboard-card p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <input value={active.name} onChange={(e) => setActive({ ...active, name: e.target.value })} className="input-pill font-bold text-lg flex-1 min-w-[12rem]" data-testid="tpl-name" />
            <div className="flex gap-2">
              <button onClick={reset} className="btn-ghost text-sm" data-testid="tpl-reset"><RotateCcw className="w-4 h-4" /> Reset</button>
              <button onClick={activate} className="btn-ghost text-sm" data-testid="tpl-activate">Set as default</button>
              <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="tpl-save"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
          <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">HTML source · WeasyPrint engine — full CSS, Flexbox, Grid, Unicode ₹, web fonts all supported</div>
          <textarea rows={14} value={active.html_body || ""} onChange={(e) => setActive({ ...active, html_body: e.target.value })} className="input-pill font-mono text-xs" data-testid="tpl-html" />
        </div>
        <div className="dashboard-card p-5">
          <div className="text-[10px] uppercase font-bold text-slate-500 mb-2">Live preview (sample data filled)</div>
          <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
            <iframe title="preview" srcDoc={previewHtml} className="w-full h-[560px] bg-white" data-testid="tpl-preview" />
          </div>
        </div>
      </div>
    </div>
  );
}

// =========================================================
// SETTINGS — prefix, period, address, logo, email-on-issue
// =========================================================
function SettingsTab() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rolloverPeriod, setRolloverPeriod] = useState("");

  const load = async () => {
    const r = await api.get("/accountant/settings"); setForm(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/accountant/settings", {
        prefix: form.prefix, billing_period: form.billing_period,
        billed_from_name: form.billed_from_name, address: form.address,
        email: form.email, contact: form.contact, logo_url: form.logo_url,
        email_on_issue: !!form.email_on_issue,
      });
      toast.success("Settings saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const rollover = async () => {
    if (!rolloverPeriod.trim()) { toast.error("Enter new period, e.g. 26-27"); return; }
    if (!window.confirm(`Roll over? Current period ${form.billing_period} (last serial: ${form.serial_counter}) will be archived. Next receipt = ${form.prefix}/${rolloverPeriod}/001.`)) return;
    try {
      await api.post("/accountant/settings/rollover-period", { new_period: rolloverPeriod.trim() });
      toast.success(`Rolled over. Now billing period ${rolloverPeriod.trim()}, serial reset to 000.`);
      setRolloverPeriod(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rollover failed"); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd);
    setForm({ ...form, logo_url: `${BACKEND_URL}${r.data.url}` });
  };

  if (!form) return <div className="p-12 text-slate-400 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-6xl">
      <div className="dashboard-card p-5 space-y-3" data-testid="settings-billing">
        <div><h4 className="font-display font-black text-lg">Receipt numbering</h4><p className="text-sm text-slate-500">Sample next receipt: <b className="font-mono">{form.prefix}/{form.billing_period}/{String((form.serial_counter || 0) + 1).padStart(3, "0")}</b></p></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-slate-500">Prefix (e.g. KL)</label><input value={form.prefix || ""} onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase() })} className="input-pill mt-1" data-testid="settings-prefix" /></div>
          <div><label className="text-xs text-slate-500">Billing period (e.g. 25-26)</label><input value={form.billing_period || ""} onChange={(e) => setForm({ ...form, billing_period: e.target.value })} className="input-pill mt-1" data-testid="settings-period" /></div>
        </div>
        <div className="text-[10px] font-bold text-orange-700 bg-orange-50 rounded-lg p-3">Serial counter (last used): {form.serial_counter}. Editing prefix / period and clicking <b>Save settings</b> retroactively renames every receipt in the current period (e.g. KL/25-26/001 → 26-27/001) and updates the journal. Historical periods archived via <b>Roll over</b> are untouched.</div>
      </div>

      <div className="dashboard-card p-5 space-y-3" data-testid="settings-rollover">
        <div><h4 className="font-display font-black text-lg">Roll over to new period</h4><p className="text-sm text-slate-500">Archives the current period. Serial resets to <b>000</b>. Historical receipts stay linked to their period.</p></div>
        <div className="flex items-center gap-2">
          <input placeholder="New period e.g. 26-27" value={rolloverPeriod} onChange={(e) => setRolloverPeriod(e.target.value)} className="input-pill flex-1" data-testid="settings-rollover-input" />
          <button onClick={rollover} className="btn-primary bg-rose-600 hover:bg-rose-700" data-testid="settings-rollover-btn">Roll over</button>
        </div>
        {(form.previous_periods || []).length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Past periods</div>
            {(form.previous_periods || []).map((p, i) => (
              <div key={i} className="text-xs text-slate-600 flex items-center justify-between py-1 border-b border-slate-50 last:border-0">
                <span className="font-mono">{p.prefix}/{p.period}</span>
                <span className="text-slate-400">Last serial: {p.last_counter} · Closed {(p.closed_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="dashboard-card p-5 space-y-3 lg:col-span-2" data-testid="settings-branding">
        <div><h4 className="font-display font-black text-lg">Branding on receipt</h4><p className="text-sm text-slate-500">Every receipt PDF is stamped with this identity. Changes apply immediately.</p></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="text-xs text-slate-500">Billed-from name</label><input value={form.billed_from_name || ""} onChange={(e) => setForm({ ...form, billed_from_name: e.target.value })} className="input-pill mt-1" data-testid="settings-name" /></div>
          <div><label className="text-xs text-slate-500">Contact number</label><input value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="input-pill mt-1" data-testid="settings-contact" /></div>
          <div><label className="text-xs text-slate-500">Email</label><input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-pill mt-1" data-testid="settings-email" /></div>
          <div><label className="text-xs text-slate-500">Address</label><input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input-pill mt-1" data-testid="settings-address" /></div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500">Logo</label>
            <div className="flex gap-2 mt-1">
              <input placeholder="https://…" value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="input-pill flex-1" data-testid="settings-logo-url" />
              <label className="btn-ghost px-3 cursor-pointer">Upload<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} /></label>
            </div>
            {form.logo_url && <img src={form.logo_url} alt="logo" className="h-14 mt-2 border rounded p-1 bg-white" />}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm mt-2">
          <input type="checkbox" checked={!!form.email_on_issue} onChange={(e) => setForm({ ...form, email_on_issue: e.target.checked })} data-testid="settings-email-toggle" />
          Auto-email PDF receipt to the payer when a receipt is issued
        </label>
        <button onClick={save} disabled={busy} className="btn-primary mt-2" data-testid="settings-save"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save settings"}</button>
      </div>
    </div>
  );
}
